import React, {Fragment, ReactNode} from 'react'
import {CONDITION_OPERATORS_BY_TYPE, conditionValue} from 'server/utils/conditions.js'
import type {Condition, ConditionOperator, ConditionType, ConditionValue} from 'server/utils/conditions.js'
import {P, Card, Button, IconButton, Colors} from '@components/UI/index.js'
import {formSpacing, FormRow, FormStack, Input, RadioGroup, Select, TimePicker} from '@components/UI/forms/index.js'
import t from 'src/helpers/Languages.js'

export interface ConditionVariableOption<V extends string> {
    value: V
    title: string
}

export interface ConditionGroupsEditorProps<V extends string> {
    conditionGroups: Condition<V>[][]
    /** En (true): all conditions within a group, Of (false): one condition within a group */
    matchAll: boolean
    onChange: (conditionGroups: Condition<V>[][], matchAll: boolean) => void
}

interface ObjectConditionGroupsEditorProps<V extends string> extends ConditionGroupsEditorProps<V> {
    variables: ConditionVariableOption<V>[]
    types: Record<V, ConditionType>
    /** The input of a variable's value, undefined for the default input of its type */
    renderValue: (condition: Condition<V>, onChange: (value: ConditionValue) => void) => ReactNode | undefined
}

/** The default input for the value of a type, a list or choice always needs the options of its variable */
const renderTypeValue = (type: ConditionType, condition: Condition, onChange: (value: ConditionValue) => void) => {
    switch (type) {
        case 'time':
            return <TimePicker value={condition.value as string} onChange={(event) => onChange(event.target.value)} />
        case 'postalCode':
            return <Input info={t('postalCodeZoneInfo')} value={condition.value as string} onChange={(event) => onChange(event.target.value)} />
        case 'boolean':
            return (
                <Select
                    value={condition.value ? 'true' : 'false'}
                    options={[{value: 'true', title: t('yes')}, {value: 'false', title: t('no')}]}
                    onChange={(event) => onChange(event.target.value === 'true')}
                />
            )
        case 'number':
            return <Input type='number' min={0} value={condition.value as number} onChange={(event) => onChange(Number(event.target.value) || 0)} />
        case 'text':
            // One text to compare with, or a list of texts for Een van / Geen van
            return Array.isArray(condition.value) ?
                <Select options={[]} values={condition.value} allowCustomValues onChange={(event) => onChange(event.target.values)} /> :
                <Input value={condition.value as string} onChange={(event) => onChange(event.target.value)} />
        default:
            return null
    }
}

/** A variable with a list as value (colli types, options, zones) offers Alle in, so its Bevat means Bevat alle */
const operatorTitle = (operator: ConditionOperator, operators: readonly ConditionOperator[]) => {
    switch (operator) {
        case 'contains':
            return operators.includes('in') ? t('conditionContainsAll') : t('contains')
        case 'ncontains':
            return t('notContains')
        case 'in':
            return t('conditionAllIn')
        case 'any':
            return t('conditionAnyOf')
        case 'none':
            return t('conditionNoneOf')
        default:
            return t(operator)
    }
}

/** A concrete explanation of an operator for the type of its variable */
const operatorInfo = (operator: ConditionOperator, type: ConditionType) => {
    switch (operator) {
        case 'eq':
            return type === 'text' ? t('conditionTextEqInfo') : t('conditionEqInfo')
        case 'neq':
            return type === 'text' ? t('conditionTextNeqInfo') : t('conditionNeqInfo')
        case 'lt':
            return type === 'time' ? t('conditionTimeLtInfo') : t('conditionLtInfo')
        case 'lte':
            return t('conditionLteInfo')
        case 'gt':
            return type === 'time' ? t('conditionTimeGtInfo') : t('conditionGtInfo')
        case 'gte':
            return t('conditionGteInfo')
        case 'contains':
            return type === 'list' ? t('conditionContainsAllInfo') : t('conditionContainsInfo')
        case 'ncontains':
            return t('conditionNotContainsInfo')
        case 'in':
            return type === 'postalCode' ? t('conditionPostalCodeAllInInfo') : t('conditionAllInInfo')
        case 'any':
            return type === 'postalCode' ? t('conditionPostalCodeAnyOfInfo') : t('conditionAnyOfInfo')
        case 'none':
            return type === 'postalCode' ? t('conditionPostalCodeNoneOfInfo') : t('conditionNoneOfInfo')
        default:
            return undefined
    }
}

/**
 * Edits groups of conditions combined with En/Of, see server/utils/conditions.ts. The variables and their values
 * come from OrderConditionGroupsEditor or ParcelConditionGroupsEditor.
 */
function ConditionGroupsEditor<V extends string>({conditionGroups, matchAll, onChange, variables, types, renderValue}: ObjectConditionGroupsEditorProps<V>) {
    const operators = (variable: V): readonly ConditionOperator[] => CONDITION_OPERATORS_BY_TYPE[types[variable]] || []

    const newCondition = (): Condition<V> => {
        const variable = variables[0].value
        const operator = operators(variable)[0]

        return {variable, operator, value: conditionValue(types[variable], operator)}
    }

    const changeGroup = (groupIndex: number, conditionGroup: Condition<V>[]) => {
        const groups = conditionGroups.map((group, i) => i === groupIndex ? conditionGroup : group).filter((group) => group.length)
        onChange(groups, matchAll)
    }

    return (
        <>
            {conditionGroups.length > 0 && (
                <FormStack style={{marginBottom: formSpacing.rowGap}}>
                    <RadioGroup
                        style={{marginTop: 12, width: 290}}
                        value={matchAll ? 'and' : 'or'}
                        options={[
                            {value: 'and', label: t('and')},
                            {value: 'or', label: t('or')}
                        ]}
                        onChange={(event) => onChange(conditionGroups, event.target.value === 'and')}
                    />
                </FormStack>
            )}

            {conditionGroups.map((conditionGroup, i) => {
                const changeCondition = (j: number, condition: Condition<V> | null) => {
                    changeGroup(i, condition ? conditionGroup.map((c, k) => k === j ? condition : c) : conditionGroup.filter((c, k) => k !== j))
                }

                return (
                    <Fragment key={`conditionGroup-${i}`}>
                        <Card style={{padding: 12, backgroundColor: Colors.white}} shadow>
                            <FormStack>
                                {conditionGroup.map((condition, j) => {
                                    const variableOperators = operators(condition.variable)
                                    const onChangeValue = (value: ConditionValue) => changeCondition(j, {...condition, value})

                                    return (
                                        <FormRow key={`condition-${i}-${j}`} style={{alignItems: 'center'}}>
                                            <div style={{width: 24}}>
                                                {j > 0 && <P style={{wordBreak: 'normal'}}>{matchAll ? t('and') : t('or')}</P>}
                                            </div>

                                            <Select
                                                style={{width: 200}}
                                                value={condition.variable}
                                                options={variables}
                                                onChange={(event) => {
                                                    const variable = event.target.value as V
                                                    const operator = operators(variable).includes(condition.operator) ? condition.operator : operators(variable)[0]

                                                    changeCondition(j, {variable, operator, value: conditionValue(types[variable], operator)})
                                                }}
                                            />

                                            <Select
                                                style={{width: 150}}
                                                value={condition.operator}
                                                info={operatorInfo(condition.operator, types[condition.variable])}
                                                options={variableOperators.map((operator) => ({value: operator, title: operatorTitle(operator, variableOperators)}))}
                                                onChange={(event) => {
                                                    const operator = event.target.value as ConditionOperator
                                                    changeCondition(j, {...condition, operator, value: conditionValue(types[condition.variable], operator, condition.value)})
                                                }}
                                            />

                                            {renderValue(condition, onChangeValue) ?? renderTypeValue(types[condition.variable], condition, onChangeValue)}

                                            <IconButton
                                                icon='mdi mdi-delete'
                                                onClick={() => changeCondition(j, null)}
                                            />
                                        </FormRow>
                                    )
                                })}

                                <Button
                                    icon='mdi mdi-plus'
                                    label={t('condition')}
                                    onClick={() => changeGroup(i, [...conditionGroup, newCondition()])}
                                    variant='outline-white'
                                />
                            </FormStack>
                        </Card>

                        {i < conditionGroups.length - 1 && (
                            <P style={{marginTop: 24, marginBottom: 24}}>{matchAll ? t('or') : t('and')}</P>
                        )}
                    </Fragment>
                )
            })}

            <Button
                icon='mdi mdi-plus'
                label={t('conditionGroup')}
                onClick={() => onChange([...conditionGroups, [newCondition()]], matchAll)}
                variant='outline-white'
                style={{marginTop: 24}}
            />
        </>
    )
}

export default ConditionGroupsEditor
